import { Component, OnChanges, Input, SimpleChanges, Injector } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ApAlertService, ApUndoableOperationService } from 'webcommon/core-ui';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FcItemModalContext, FcItemModalMode } from '../../../models';
import { FormGroupExtended } from '../../generic/fc-list-validation';
import { FcInsurancePayerListItemDto, FcInsurancePayerNewCreatedDto, FcInsurancePlanListItemDto, FormGroupValidationRules } from 'webcommon/shared';
import { debounceTime } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { FcItemListBaseComponent } from '../../generic';

@Component({
    providers: [ApAlertService, FcListValidationService],
    selector: 'fc-insurance-plan-contact-info-tab',
    styleUrls: ['./fc-insurance-plan-contact-info-tab.component.less'],
    templateUrl: './fc-insurance-plan-contact-info-tab.component.html',
})
export class FcInsurancePlanContactInfoTabComponent extends FcItemListBaseComponent implements OnChanges {
    @Input() baseId: string;
    @Input() form: FormGroup;
    @Input() listItem: FcInsurancePlanListItemDto;
    @Input() isSubmitted = false;
    @Input() itemContext: FcItemModalContext;
    @Input() isDataLoaded: boolean;

    public disableAddressListControls: BehaviorSubject<boolean[] | null> = new BehaviorSubject<boolean[] | null>([true, false]);
    public selectedInsurancePayerPks: string[] = [];
    public insurancePayerData: FcInsurancePayerListItemDto;
    public firstRenderOnModify = false;
    public isInsurancePayerValueChanged = false;
    public isNewInsurancePayerCreated = false;

    private readonly waitTime: number = 300;

    // Form Controls
    public externalIdControl: FormControl = new FormControl('');
    public nameControl: FormControl = new FormControl('');
    public primaryContactControl: FormControl = new FormControl('');
    public secondaryContactControl: FormControl = new FormControl('');
    public notesControl: FormControl = new FormControl('');
    public inactiveControl: FormControl = new FormControl(false);
    public useCarrierAddressControl: FormControl = new FormControl(true);
    public insuranceCarrierUidControl: FormControl = new FormControl('');

    // Form Group
    public addressGroup: FormGroupExtended = new FormGroupExtended({});
    public phoneOneGroup: FormGroup = new FormGroup({});
    public phoneTwoGroup: FormGroup = new FormGroup({});
    public phoneThreeGroup: FormGroup = new FormGroup({});
    public phoneFourGroup: FormGroup = new FormGroup({});

    public listItemDefaultValidation: FormGroupValidationRules = {
        ExternalID: {
            MaxLength: 25,
        },
        Name: {
            IsRequired: true,
            MaxLength: 100,
        },
        Contact: {
            MaxLength: 50,
        },
        InsuranceCarrierUid: {
            IsRequired: true,
        },
        Notes: {
            IsRequired: false,
            MaxLength: 8000,
        },
    };

    constructor(
        injector: Injector,
        protected apUndoableOperationService: ApUndoableOperationService) {
        super(injector);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.itemContext != null) {
            this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
            if (this.itemContext.mode === FcItemModalMode.Modify) {
                this.firstRenderOnModify = true;
            }
            if (this.itemContext.mode === FcItemModalMode.New) {
                this.isDataLoaded = true;
            }
        }

        if (changes.form != null) {
            this.addControlsToForm();
        }
        if ((changes.itemContext != null || changes.form != null || changes.listItem != null) && this.isDataLoaded) {
            this.setForm();
        }

        this.useCarrierAddressControl.valueChanges.pipe(debounceTime(this.waitTime), this.takeUntilDestroy()).subscribe(() => {
            switch (true) {
                case this.useCarrierAddressControl.value && !this.firstRenderOnModify:
                    this.disableAddressListControls.next([this.useCarrierAddressControl.value, true]);
                    if (this.insurancePayerData?.Corporation?.Address) {
                        this.setValuesInAddressAndPhone(this.insurancePayerData);
                    }
                    break;
                case !this.useCarrierAddressControl.value && !this.firstRenderOnModify:
                    this.disableAddressListControls.next([this.useCarrierAddressControl.value, true]);
                    break;
                default:
                    this.disableAddressListControls.next([this.useCarrierAddressControl.value, false]);
            }
            this.firstRenderOnModify = false;
        });
    }

    private addControlsToForm(): void {
        this.form.addControl('externalId', this.externalIdControl);
        this.form.addControl('name', this.nameControl);
        this.form.addControl('notes', this.notesControl);
        this.form.addControl('inactive', this.inactiveControl);
        this.form.addControl('useCarrierAddress', this.useCarrierAddressControl);
        this.form.addControl('insuranceCarrierUid', this.insuranceCarrierUidControl);
        this.form.addControl('address', this.addressGroup);
        this.form.addControl('phoneOne', this.phoneOneGroup);
        this.form.addControl('phoneTwo', this.phoneTwoGroup);
        this.form.addControl('phoneThree', this.phoneThreeGroup);
        this.form.addControl('phoneFour', this.phoneFourGroup);
    }

    public setForm(): void {
        // External ID Control
        this.externalIdControl.setValidators(this.fcListValidation.getValidators('ExternalID'));
        this.externalIdControl.setValue(this.listItem.ExternalID);

        // Name Control
        this.nameControl.setValidators(this.fcListValidation.getValidators('Name'));
        this.nameControl.setValue(this.listItem.Name);

        // Notes Control
        this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
        this.notesControl.setValue(this.listItem.Notes);

        // Inactive Control
        this.inactiveControl.setValue(this.listItem.Inactive);

        // Use Payer's Address Control
        this.useCarrierAddressControl.setValue(this.listItem.UseCarrierAddress);

        // InsuranceCarrierUid Control
        this.insuranceCarrierUidControl.setValidators(this.fcListValidation.getValidators('InsuranceCarrierUid'));

        this.selectedInsurancePayerPks = this.getFindControlValue(this.listItem.InsuranceCarrierUid);
    }

    public onSetAreaCode(areaCode: string): void {
        const phoneNumberControl = this.phoneOneGroup.get('phoneNumber');
        if (phoneNumberControl != null && !phoneNumberControl.value) {
            phoneNumberControl.patchValue(areaCode);
            phoneNumberControl.markAsDirty();
        }
    }

    public getFindControlValidation(controlName: string): boolean {
        if (this.fcListValidation.validations[controlName]) {
            return !!this.fcListValidation.validations[controlName].IsRequired;
        } else {
            return !!this.listItemDefaultValidation[controlName].IsRequired;
        }
    }

    public insurancePayerChanged(event: boolean) {
        this.isInsurancePayerValueChanged = event;
    }

    public onInsurancePayerDataPopulate(insurancePayerReceivedData: FcInsurancePayerListItemDto[]): void {
        this.insurancePayerData = { ...insurancePayerReceivedData[0] };
        if (this.isInsurancePayerValueChanged || this.isNewInsurancePayerCreated) {
            this.setValuesInAddressAndPhone(this.insurancePayerData);
        }
        this.isNewInsurancePayerCreated = false;
    }

    public setValuesInAddressAndPhone(insurancePayerData: FcInsurancePayerListItemDto): void {
        if (this.useCarrierAddressControl.value) {
            if (insurancePayerData.Corporation.Address) {
                this.listItem.Address = {
                    Address1: insurancePayerData.Corporation.Address?.Address1,
                    Address2: insurancePayerData.Corporation.Address?.Address2,
                    City: insurancePayerData.Corporation.Address?.City,
                    State: insurancePayerData.Corporation.Address?.State,
                    Zip: insurancePayerData.Corporation.Address?.Zip,
                    ZipUid: insurancePayerData.Corporation.Address?.ZipUid,
                    Country: insurancePayerData.Corporation.Address?.Country,
                    CountyName: insurancePayerData.Corporation.Address?.CountyName,
                    CountyCode: insurancePayerData.Corporation.Address?.CountyCode,
                };
            } else {
                this.listItem.Address = {
                    Address1: '',
                    Address2: '',
                    City: '',
                    State: '',
                    Zip: '',
                    ZipUid: '',
                    Country: '',
                    CountyName: '',
                    CountyCode: '',
                };
            }
            if (insurancePayerData.Corporation.Phone1) {
                this.listItem.Phone1 = {
                    PhoneNumber: insurancePayerData.Corporation.Phone1?.PhoneNumber,
                    PhoneType: insurancePayerData.Corporation.Phone1?.PhoneType,
                    PhoneTypeUid: insurancePayerData.Corporation.Phone1?.PhoneTypeUid,
                };
            } else {
                this.listItem.Phone1 = {
                    PhoneNumber: '',
                    PhoneType: '',
                    PhoneTypeUid: '',
                };
            }
            if (insurancePayerData.Corporation.Phone2) {
                this.listItem.Phone2 = {
                    PhoneNumber: insurancePayerData.Corporation.Phone2?.PhoneNumber,
                    PhoneType: insurancePayerData.Corporation.Phone2?.PhoneType,
                    PhoneTypeUid: insurancePayerData.Corporation.Phone2?.PhoneTypeUid,
                };
            } else {
                this.listItem.Phone2 = {
                    PhoneNumber: '',
                    PhoneType: '',
                    PhoneTypeUid: '',
                };
            }
            if (insurancePayerData.Corporation.Phone3) {
                this.listItem.Phone3 = {
                    PhoneNumber: insurancePayerData.Corporation.Phone3?.PhoneNumber,
                    PhoneType: insurancePayerData.Corporation.Phone3?.PhoneType,
                    PhoneTypeUid: insurancePayerData.Corporation.Phone3?.PhoneTypeUid,
                };
            } else {
                this.listItem.Phone3 = {
                    PhoneNumber: '',
                    PhoneType: '',
                    PhoneTypeUid: '',
                };
            }
            if (insurancePayerData.Corporation.Phone4) {
                this.listItem.Phone4 = {
                    PhoneNumber: insurancePayerData.Corporation.Phone4?.PhoneNumber,
                    PhoneType: insurancePayerData.Corporation.Phone4?.PhoneType,
                    PhoneTypeUid: insurancePayerData.Corporation.Phone4?.PhoneTypeUid,
                };
            } else {
                this.listItem.Phone4 = {
                    PhoneNumber: '',
                    PhoneType: '',
                    PhoneTypeUid: '',
                };
            }
        }
    }

    public setInsurancePayer(insurancePayerData: FcInsurancePayerNewCreatedDto): void {
        this.isNewInsurancePayerCreated = insurancePayerData.isSuccess;
        this.selectedInsurancePayerPks = [insurancePayerData.resultValue.CorporationUid];
    }
}
